'use client'; 
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { supabaseBrowser } from '@/lib/supabaseBrowser';
import { useRouter } from 'next/navigation';

type Post = {
  id: string;
  prompt: string;
  template_type: string;
  created_at: string;
  closes_at?: string;
  is_open?: boolean;
  response_count?: number;
  owner_user_id?: string;
  tags?: string[];
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

const TYPE_LABELS: Record<string, string> = {
  pick_one: 'Poll', likert: 'Rating', text: 'Open Response', yes_no: 'Yes | No', rank_order: 'Rank Order',
};
const TYPE_SYMBOLS: Record<string, string> = {
  pick_one: '⊡', likert: '★', text: '✎', yes_no: '/', rank_order: '⇅',
};

const TAG_STRUCTURE: Record<string, string[]> = {
  Business:      ['Naming', 'Pricing', 'Marketing', 'Product'],
  Design:        ['Logo & Visual', 'UX & Flow', 'Branding'],
  Tech:          ['Features', 'Dev', 'Tools'],
  Lifestyle:     ['Fashion & Beauty', 'Food & Drink', 'Health', 'Travel'],
  Hobbies:       ['Gaming', 'Outdoors', 'Crafts', 'Home & Garden'],
  Entertainment: ['Pop Culture', 'Celebrities', 'Sports', 'Media'],
  Social:        ['Relationships', 'Life Decisions', 'Opinions'],
  Other:         [],
};

const ALL_SUBS = Object.values(TAG_STRUCTURE).flat();

function matchesBroadTag(postTags: string[], broad: string): boolean {
  const subs = TAG_STRUCTURE[broad] ?? [];
  if (subs.length === 0) return postTags.some(t => !ALL_SUBS.includes(t));
  return postTags.some(t => subs.includes(t));
}

// Single toggle, not a third tab and not two labeled buttons -- unlabeled
// (just a checkmark icon) in its default/off state since that's the
// expected queue view, and only shows the word "Answered" once switched on.
function AnsweredToggle({ active, onClick }: { active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-label={active ? 'Show queue' : 'Show answered'}
      style={{
        display: 'flex', alignItems: 'center', gap: '4px',
        fontSize: '11px', fontWeight: 500,
        color: active ? 'var(--green)' : 'var(--text-dim)',
        background: active ? 'var(--green-glow)' : 'none',
        border: `1px solid ${active ? 'var(--green-dim)' : 'var(--border)'}`,
        borderRadius: '20px', padding: '3px 9px', cursor: 'pointer',
        fontFamily: 'DM Sans, sans-serif', flexShrink: 0,
      }}
    >
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {active && 'Answered'}
    </button>
  );
}

export default function Home() {
  const router = useRouter();
  const supabase = supabaseBrowser();
  const [userHandle, setUserHandle] = useState<string | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [myPosts, setMyPosts] = useState<Post[]>([]);
  const [respondQueue, setRespondQueue] = useState<Post[]>([]);
  // Response history for the Responder tab's "Answered" toggle -- the exact
  // inverse of respondQueue's filter (same posts/respondedIds data, just
  // the other side of it), so no extra query is needed for this.
  const [answeredPosts, setAnsweredPosts] = useState<Post[]>([]);
  // Single shared toggle, not a third tab and not two labeled buttons --
  // default (false) shows the existing queue with no label needed since
  // that's the expected view; true flips to response history and is the
  // only state that gets an explicit "Answered" label. Shared between the
  // sidebar and the expanded dashboard modal since they show the same
  // underlying view.
  const [showAnswered, setShowAnswered] = useState(false);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [sidebarTab, setSidebarTab] = useState<'poster' | 'responder'>('poster');
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(false);

  // Escape-to-close for the pop-out dashboard. Without this, a keyboard-only
  // user who opens it has literally no way to close it — clicking the
  // backdrop or the X button are both mouse-only interactions.
  useEffect(() => {
    if (!dashboardOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setDashboardOpen(false);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dashboardOpen]);
  const [clarityMap, setClarityMap] = useState<Record<string, { color: string; symbol: string }>>({});
  const [activeTag, setActiveTag] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data: rawData } = await supabase
        .from('posts').select('id,prompt,template_type,created_at,closes_at,is_open,owner_user_id,tags')
        .order('created_at', { ascending: false });
      const rawPosts = (rawData || []) as Post[];

      // Response counts now come from a dedicated public endpoint that computes them
      // server-side, bypassing the RLS restriction that was making non-owners see
      // 0 or 1 instead of the true count. See /api/response-counts.
      let countMap: Record<string, number> = {};
      try {
        const countsRes = await fetch('/api/response-counts');
        if (countsRes.ok) {
          const countsData = await countsRes.json();
          countMap = countsData.counts || {};
        }
      } catch {
        // Non-fatal — counts will just show as 0 if this fails, rather than breaking the feed
      }

      const withCounts = rawPosts.map(p => ({
        ...p,
        response_count: countMap[p.id] ?? 0,
      }));

      setPosts(withCounts);
      setLoadingPosts(false);

      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      setAuthLoading(false);

      if (!user) return;
      setUserEmail(user.email ?? null);
      setUserId(user.id);

      const { data: profile } = await supabase
        .from('profiles').select('user_id, handle').eq('user_id', user.id).maybeSingle();
      if (!profile) { router.push('/onboarding'); return; }
      setUserHandle(profile.handle ?? null);

      const { data: myResponses } = await supabase
        .from('responses').select('post_id').eq('rater_user_id', user.id);
      const respondedIds = new Set((myResponses || []).map(r => r.post_id));

      setMyPosts(withCounts.filter(p => p.owner_user_id === user.id));
      setRespondQueue(withCounts.filter(p => p.owner_user_id !== user.id && !respondedIds.has(p.id)));
      setAnsweredPosts(withCounts.filter(p => p.owner_user_id !== user.id && respondedIds.has(p.id)));

      const cm: Record<string, { color: string; symbol: string }> = {};
      for (const p of withCounts.filter(p => p.owner_user_id === user.id)) {
        if (!p.response_count || p.response_count < 2) { cm[p.id] = { color: 'var(--text-dim)', symbol: '·' }; continue; }
        const { data: resp } = await supabase.from('responses').select('likert_value,pick_one_option_id').eq('post_id', p.id);
        if (!resp) continue;
        if (p.template_type === 'likert') {
          const vals = resp.map(r => r.likert_value).filter((v): v is number => typeof v === 'number');
          if (vals.length < 2) continue;
          const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
          const sd = Math.sqrt(vals.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / vals.length);
          if (sd < 1.0) cm[p.id] = { color: 'var(--green)', symbol: '✦' };
          else if (sd < 1.5) cm[p.id] = { color: 'var(--amber)', symbol: '~' };
          else cm[p.id] = { color: 'var(--danger)', symbol: '≠' };
        } else if (p.template_type === 'pick_one' || p.template_type === 'yes_no') {
          const counts: Record<string, number> = {};
          resp.forEach(r => { if (r.pick_one_option_id) counts[r.pick_one_option_id] = (counts[r.pick_one_option_id] || 0) + 1; });
          const total = resp.length;
          const top = Math.max(...Object.values(counts).concat(0));
          const topPct = top / total;
          if (p.template_type === 'yes_no') {
            if (topPct >= 0.70) cm[p.id] = { color: 'var(--green)', symbol: '✦' };
            else if (topPct >= 0.55) cm[p.id] = { color: 'var(--amber)', symbol: '~' };
            else cm[p.id] = { color: 'var(--danger)', symbol: '≠' };
          } else {
            cm[p.id] = topPct >= 0.66
              ? { color: 'var(--green)', symbol: '✦' }
              : topPct >= 0.5
              ? { color: 'var(--amber)', symbol: '~' }
              : { color: 'var(--danger)', symbol: '≠' };
          }
        } else {
          cm[p.id] = { color: 'var(--text-dim)', symbol: '·' };
        }
      }
      setClarityMap(cm);
    })();
  }, [router]);

  return (
    <>
      {/* Botanical vines */}
      <div style={{ position: 'fixed', top: 0, bottom: 0, left: 0, width: 'clamp(80px, 10vw, 160px)', pointerEvents: 'none', zIndex: 0 }}>
        <svg width="100%" height="100%" viewBox="0 0 160 1000" preserveAspectRatio="xMaxYMid slice" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M105 1000 Q99 870 109 750 Q117 630 101 520 Q87 420 103 320 Q115 230 99 140 Q87 70 103 0" stroke="#4a7a54" strokeWidth="4" fill="none" strokeLinecap="round"/>
          <path d="M101 520 Q121 480 131 445" stroke="#3d6647" strokeWidth="2.2" fill="none" strokeLinecap="round"/>
          <path d="M103 320 Q123 285 135 252" stroke="#3d6647" strokeWidth="2" fill="none" strokeLinecap="round"/>
          <path d="M103 920 Q42 895 16 848 Q55 843 103 896" fill="#3d6647"/>
          <path d="M103 920 Q44 898 18 851" stroke="#5a8c64" strokeWidth="1.5" fill="none" opacity="0.6"/>
          <path d="M105 940 Q145 910 155 872 Q130 882 105 918" fill="#4a5a3e" opacity="0.9"/>
          <path d="M101 890 Q55 868 32 834 Q66 832 101 866" fill="#5a8c64" opacity="0.7"/>
          <circle cx="16" cy="847" r="7" fill="#9fd4aa" opacity="0.9"/>
          <circle cx="10" cy="840" r="5" fill="#c8e8d0" opacity="0.8"/>
          <circle cx="20" cy="838" r="4" fill="#9fd4aa" opacity="0.7"/>
          <circle cx="13" cy="830" r="3" fill="#e0f4e8" opacity="0.6"/>
          <path d="M101 750 Q36 722 10 678 Q52 675 101 726" fill="#3d6647"/>
          <path d="M101 750 Q38 725 12 681" stroke="#5a8c64" strokeWidth="1.5" fill="none" opacity="0.6"/>
          <path d="M103 768 Q145 738 153 700 Q128 712 103 746" fill="#4a5a3e" opacity="0.85"/>
          <path d="M99 710 Q60 688 44 660 Q72 660 99 688" fill="#6aad78" opacity="0.65"/>
          <circle cx="11" cy="677" r="6" fill="#9fd4aa" opacity="0.85"/>
          <circle cx="5" cy="671" r="4" fill="#c8e8d0" opacity="0.75"/>
          <circle cx="16" cy="669" r="3.5" fill="#9fd4aa" opacity="0.65"/>
          <path d="M101 520 Q28 490 6 446 Q52 446 101 498" fill="#3d6647"/>
          <path d="M101 520 Q30 493 8 449" stroke="#5a8c64" strokeWidth="1.5" fill="none" opacity="0.6"/>
          <path d="M131 445 Q155 412 157 376 Q136 390 131 425" fill="#4a5a3e" opacity="0.8"/>
          <path d="M99 482 Q58 460 42 432 Q66 434 99 462" fill="#5a8c64" opacity="0.6"/>
          <path d="M103 320 Q30 292 8 250 Q52 252 103 298" fill="#3d6647"/>
          <path d="M103 320 Q32 295 10 253" stroke="#5a8c64" strokeWidth="1.5" fill="none" opacity="0.55"/>
          <path d="M135 252 Q159 218 159 182 Q138 198 135 232" fill="#4a5a3e" opacity="0.75"/>
          <circle cx="8" cy="249" r="6" fill="#9fd4aa" opacity="0.8"/>
          <circle cx="2" cy="243" r="4" fill="#c8e8d0" opacity="0.7"/>
          <circle cx="13" cy="241" r="3" fill="#9fd4aa" opacity="0.6"/>
          <path d="M99 140 Q26 112 6 72 Q48 76 99 118" fill="#3d6647" opacity="0.9"/>
          <path d="M101 100 Q130 72 134 38 Q114 52 101 82" fill="#4a5a3e" opacity="0.7"/>
        </svg>
      </div>

      <div style={{ position: 'fixed', top: 0, bottom: 0, right: 0, width: 'clamp(80px, 10vw, 160px)', pointerEvents: 'none', zIndex: 0 }}>
        <svg width="100%" height="100%" viewBox="0 0 160 1000" preserveAspectRatio="xMinYMid slice" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M55 1000 Q61 880 50 760 Q41 640 57 530 Q70 430 52 330 Q38 240 56 150 Q68 78 50 0" stroke="#4a7a54" strokeWidth="4" fill="none" strokeLinecap="round"/>
          <path d="M57 530 Q36 495 26 455" stroke="#3d6647" strokeWidth="2.2" fill="none" strokeLinecap="round"/>
          <path d="M52 330 Q30 298 20 262" stroke="#3d6647" strokeWidth="2" fill="none" strokeLinecap="round"/>
          <path d="M57 820 Q80 800 90 778 Q98 758 88 742" stroke="#6aad78" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.8"/>
          <path d="M88 742 Q96 728 86 716" stroke="#6aad78" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.6"/>
          <path d="M57 600 Q82 578 94 554 Q102 532 90 514" stroke="#6aad78" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.75"/>
          <path d="M52 380 Q78 356 90 330 Q100 306 88 288" stroke="#6aad78" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.7"/>
          <path d="M52 200 Q76 176 86 150 Q94 126 82 108" stroke="#6aad78" strokeWidth="1.8" fill="none" strokeLinecap="round" opacity="0.65"/>
          <path d="M55 940 Q116 914 140 868 Q98 864 55 916" fill="#3d6647"/>
          <path d="M53 900 Q10 876 -4 840 Q20 840 53 876" fill="#4a5a3e" opacity="0.85"/>
          <circle cx="90" cy="740" r="5" fill="#9fd4aa" opacity="0.9"/>
          <circle cx="97" cy="746" r="4" fill="#9fd4aa" opacity="0.8"/>
          <circle cx="84" cy="748" r="3.5" fill="#c8e8d0" opacity="0.75"/>
          <circle cx="92" cy="752" r="3" fill="#9fd4aa" opacity="0.65"/>
          <path d="M50 760 Q114 734 136 690 Q94 688 50 736" fill="#3d6647"/>
          <path d="M52 720 Q10 696 -2 660 Q22 662 52 698" fill="#4a5a3e" opacity="0.75"/>
          <path d="M57 530 Q122 502 144 458 Q100 458 57 508" fill="#3d6647"/>
          <path d="M26 455 Q-2 424 -4 388 Q18 402 26 436" fill="#4a5a3e" opacity="0.7"/>
          <circle cx="90" cy="512" r="5" fill="#9fd4aa" opacity="0.85"/>
          <circle cx="97" cy="518" r="4" fill="#9fd4aa" opacity="0.75"/>
          <circle cx="84" cy="520" r="3.5" fill="#c8e8d0" opacity="0.7"/>
          <path d="M52 330 Q118 302 138 260 Q96 262 52 308" fill="#3d6647"/>
          <path d="M20 262 Q-6 232 -6 196 Q14 212 20 244" fill="#4a5a3e" opacity="0.65"/>
          <circle cx="88" cy="286" r="5" fill="#9fd4aa" opacity="0.8"/>
          <circle cx="95" cy="292" r="4" fill="#9fd4aa" opacity="0.7"/>
          <circle cx="82" cy="294" r="3.5" fill="#c8e8d0" opacity="0.65"/>
          <path d="M56 150 Q122 120 140 80 Q98 84 56 128" fill="#3d6647" opacity="0.88"/>
          <path d="M54 110 Q12 84 4 48 Q28 60 54 92" fill="#4a5a3e" opacity="0.7"/>
          <path d="M50 80 Q72 58 80 34 Q86 12 76 -4" stroke="#6aad78" strokeWidth="1.8" fill="none" strokeLinecap="round" opacity="0.7"/>
        </svg>
      </div>

      <main style={{ position: 'relative', zIndex: 1, maxWidth: '1100px', margin: '0 auto', padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

        {/* Auth status -- minHeight reserves space for the "Sign in" button
            up front, so swapping from "Checking session…" to the real
            content doesn't change this row's height. This row sits at the
            very top of <main>, so any height change here pushes the hero,
            feed, and sidebar down by the same amount -- Chrome's Layout
            Instability API then counts nearly the entire visible page as
            having "moved," which is exactly why this one small row was
            responsible for the dominant desktop CLS shift (0.593),
            completely unaffected by the grid-layout and font-loading fixes
            since neither of those touched this element at all. */}
        <div style={{ fontSize: '13px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '10px', minHeight: '28px' }}>
          {authLoading ? 'Checking session…' : userEmail ? (
            <>Signed in as <span style={{ color: 'var(--text)', fontWeight: 500 }}>{userHandle ?? userEmail}</span></>
          ) : (
            <>
              <span>You&apos;re not signed in</span>
              <a href="/login" className="btn btn-primary" style={{ fontSize: '12px', textDecoration: 'none', padding: '4px 12px' }}>Sign in</a>
            </>
          )}
        </div>

        {/* Hero */}
        <section className="card hero-card" style={{ borderColor: 'var(--green-dim)', padding: '28px 24px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '55%', background: 'linear-gradient(to right, var(--hero-fade) 0%, transparent 100%)', pointerEvents: 'none', zIndex: 1 }} />
          <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '120px', background: 'linear-gradient(to right, transparent 0%, var(--hero-fade-soft) 100%)', pointerEvents: 'none', zIndex: 1 }} />

          {/* Kawaii plant */}
          <div style={{ position: 'absolute', top: '12px', right: '20px', width: '100px', height: '120px', pointerEvents: 'none', zIndex: 0 }}>
            <svg width="100" height="120" viewBox="0 0 100 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g style={{ transformOrigin: 'bottom center', animation: 'bob 4s ease-in-out infinite' }}>
                <path d="M32 96 L68 96 L64 114 L36 114 Z" fill="#3d4a3e" opacity="0.9"/>
                <rect x="28" y="91" width="44" height="8" rx="3" fill="#4a5a4c" opacity="0.9"/>
                <rect x="32" y="92" width="36" height="2.5" rx="1" fill="#5a6e5c" opacity="0.5"/>
                <path d="M50 91 Q48 75 50 58 Q52 44 50 28" stroke="#5a8c64" strokeWidth="3" strokeLinecap="round" fill="none"/>
                <g style={{ transformOrigin: 'right center', animation: 'leaf-left 4s ease-in-out infinite' }}>
                  <path d="M50 62 Q28 50 20 30 Q34 37 50 56" fill="#6aad78" opacity="0.9"/>
                  <path d="M50 62 Q35 47 23 31" stroke="#5a9668" strokeWidth="0.8" strokeLinecap="round" opacity="0.5"/>
                </g>
                <g style={{ transformOrigin: 'left center', animation: 'leaf-right 4s ease-in-out infinite 0.3s' }}>
                  <path d="M50 50 Q72 38 78 18 Q65 26 50 46" fill="#7db68a" opacity="0.9"/>
                  <path d="M50 50 Q64 35 76 19" stroke="#6aa37a" strokeWidth="0.8" strokeLinecap="round" opacity="0.5"/>
                </g>
                <g style={{ transformOrigin: 'right center', animation: 'leaf-left 5s ease-in-out infinite 0.8s' }}>
                  <path d="M50 75 Q33 68 29 55 Q39 59 50 71" fill="#6aad78" opacity="0.7"/>
                </g>
                <g style={{ transformOrigin: 'left center', animation: 'leaf-right 4s ease-in-out infinite 0.3s' }}>
                  <path d="M50 66 Q64 59 68 48 Q59 53 50 63" fill="#7db68a" opacity="0.6"/>
                </g>
                <ellipse cx="50" cy="24" rx="11" ry="12" fill="#7db68a"/>
                <ellipse cx="47" cy="20" rx="3.5" ry="2.5" fill="#9fd4aa" opacity="0.4" transform="rotate(-15 47 20)"/>
                <g style={{ animation: 'blink 5s ease-in-out infinite 2s', transformOrigin: 'center center', transformBox: 'fill-box' } as React.CSSProperties}>
                  <ellipse cx="45" cy="23" rx="2.2" ry="2.4" fill="#2a3d2e"/>
                  <ellipse cx="55" cy="23" rx="2.2" ry="2.4" fill="#2a3d2e"/>
                  <circle cx="46" cy="22" r="0.8" fill="white" opacity="0.8"/>
                  <circle cx="56" cy="22" r="0.8" fill="white" opacity="0.8"/>
                </g>
                <path d="M45 28 Q50 32 55 28" stroke="#2a3d2e" strokeWidth="1.3" strokeLinecap="round" fill="none"/>
                <ellipse cx="41" cy="27" rx="3" ry="2" fill="#ff9eb5" opacity="0.35"/>
                <ellipse cx="59" cy="27" rx="3" ry="2" fill="#ff9eb5" opacity="0.35"/>
                <path d="M50 12 Q48 5 42 3" stroke="#5a8c64" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                <ellipse cx="41" cy="2" rx="4" ry="2.8" fill="#7db68a" opacity="0.8" transform="rotate(-20 41 2)"/>
              </g>
            </svg>
          </div>

          <style>{`
            @keyframes bob { 0%,100%{transform:translateY(0) rotate(-2deg)}50%{transform:translateY(-3px) rotate(2deg)} }
            @keyframes leaf-left { 0%,100%{transform:rotate(0)}50%{transform:rotate(-6deg)} }
            @keyframes leaf-right { 0%,100%{transform:rotate(0)}50%{transform:rotate(6deg)} }
            @keyframes blink { 0%,92%,100%{transform:scaleY(1)}96%{transform:scaleY(0.25)} }
          `}</style>

          <div style={{ position: 'relative', zIndex: 2 }}>
            <h1 style={{ fontFamily: 'Instrument Serif, serif', fontSize: '32px', lineHeight: 1.15, letterSpacing: '-0.5px', color: 'var(--text)', marginBottom: '8px' }}>
              Reduce the <em style={{ fontStyle: 'italic', color: 'var(--green)' }}>noise.</em>
            </h1>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '4px' }}>Ask the crowd. Trust the answer.</p>
            <p style={{ fontSize: '13px', color: 'var(--text-dim)', fontStyle: 'italic', marginBottom: '22px' }}>Cultivate signal.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              <Link href="/new/pickone" className="btn btn-primary" style={{ textDecoration: 'none' }}>⊡ Poll</Link>
              <Link href="/new/likert" className="btn btn-primary" style={{ textDecoration: 'none' }}>★ Rating</Link>
              <Link href="/new/text" className="btn btn-primary" style={{ textDecoration: 'none' }}>✎ Open Response</Link>
              <Link href="/new/yesno" className="btn btn-primary" style={{ textDecoration: 'none' }}>/ Yes | No</Link>
              <Link href="/new/rankorder" className="btn btn-primary" style={{ textDecoration: 'none' }}>⇅ Rank Order</Link>
            </div>
          </div>
        </section>

        {/* Below hero — feed + sidebar */}
        {/* Layout entirely via CSS (#feed-layout in globals.css), not a
            JS isMounted+window.innerWidth check -- that pattern forced
            single-column on the server AND on first paint, then reflowed
            the whole page to two columns once JS mounted and measured the
            real viewport. That was the single biggest contributor to
            desktop's 0.784 Cumulative Layout Shift score (this exact
            element was flagged directly, at a 0.593 shift). A CSS media
            query picks the right layout at first paint with no JS
            involved, so there's nothing to reflow after mount. */}
        <div id="feed-layout" style={{ gap: '16px', alignItems: 'start' }}>

          {/* Feed */}
          <section className="card" style={{ padding: '16px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
              <h2 style={{ fontFamily: 'Instrument Serif, serif', fontSize: '18px', color: 'var(--text)', letterSpacing: '-0.2px' }}>Recent posts</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {Object.keys(TAG_STRUCTURE).map(tag => (
                  <button key={tag} onClick={() => setActiveTag(activeTag === tag ? null : tag)} style={{
                    padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 500, cursor: 'pointer',
                    background: activeTag === tag ? 'var(--green-glow)' : 'var(--surface-raised)',
                    border: `1px solid ${activeTag === tag ? 'var(--green)' : 'var(--border)'}`,
                    color: activeTag === tag ? 'var(--green)' : 'var(--text-dim)',
                    fontFamily: 'DM Sans, sans-serif',
                  }}>
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {loadingPosts ? (
              <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Loading…</p>
            ) : posts.length === 0 ? (
              <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>No posts yet. Create one above.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {posts
                  .filter(p => !activeTag || (p.tags && p.tags.length > 0 && matchesBroadTag(p.tags, activeTag)))
                  .map((p) => (
                    <Link key={p.id} href={`/post/${p.id}`} style={{ display: 'block', textDecoration: 'none', background: 'var(--surface)', border: '1px solid var(--border-soft)', borderLeft: '3px solid transparent', borderRadius: '10px', padding: '14px 16px', transition: 'all 0.2s' }}
                      onMouseEnter={e => { const el = e.currentTarget as HTMLElement; el.style.background = 'var(--surface-raised)'; el.style.borderLeftColor = 'var(--green)'; el.style.transform = 'translateX(2px)'; }}
                      onMouseLeave={e => { const el = e.currentTarget as HTMLElement; el.style.background = 'var(--surface)'; el.style.borderLeftColor = 'transparent'; el.style.transform = 'translateX(0)'; }}
                    >
                      <div style={{ fontSize: '15px', color: 'var(--text)', marginBottom: '8px', lineHeight: 1.4 }}>{p.prompt}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: 'var(--text-dim)', fontStyle: 'italic', flexWrap: 'wrap' }}>
                        <span style={{ background: 'var(--surface-raised)', border: '1px solid var(--border)', borderRadius: '10px', padding: '2px 8px', fontSize: '11px', color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <span>{TYPE_SYMBOLS[p.template_type] ?? ''}</span>
                          <span>{TYPE_LABELS[p.template_type] ?? p.template_type}</span>
                        </span>
                        <span>·</span>
                        <span>{p.response_count} {p.response_count === 1 ? 'response' : 'responses'}</span>
                        <span>·</span>
                        <span>{timeAgo(p.created_at)}</span>
                        {p.owner_user_id === userId && <span style={{ color: 'var(--indigo)', fontSize: '11px' }}>✦ yours</span>}
                        {p.tags && p.tags.length > 0 && p.tags.map(tag => (
                          <span key={tag} style={{ background: 'var(--surface-raised)', border: '1px solid var(--border)', borderRadius: '10px', padding: '2px 8px', fontSize: '11px', color: 'var(--text-dim)', fontStyle: 'italic' }}>{tag}</span>
                        ))}
                      </div>
                    </Link>
                  ))}
                {activeTag && posts.filter(p => p.tags && p.tags.length > 0 && matchesBroadTag(p.tags, activeTag)).length === 0 && (
                  <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>No posts tagged "{activeTag}" yet.</p>
                )}
              </div>
            )}
          </section>

          {/* Sidebar */}
          <div className="sidebar-order" style={{
            display: 'flex', flexDirection: 'column', gap: '12px', position: 'sticky', top: '24px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', paddingLeft: '2px' }}>Your signal</div>
              <button
                onClick={() => setDashboardOpen(true)}
                title="Open as full dashboard"
                aria-label="Open as full dashboard"
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  fontSize: '11px', color: 'var(--text-dim)', fontStyle: 'italic', background: 'none',
                  border: '1px solid var(--border)', borderRadius: '6px',
                  padding: '3px 8px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                }}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                  <path d="M9 3H3v6M15 21h6v-6M21 3l-7 7M3 21l7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Expand
              </button>
            </div>
            <div className="card" style={{ overflow: 'hidden' }}>
              <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
                {(['poster', 'responder'] as const).map(tab => (
                  <button key={tab} onClick={() => setSidebarTab(tab)} style={{
                    flex: 1, padding: '10px 12px', fontSize: '12px', fontWeight: 500,
                    color: sidebarTab === tab ? 'var(--green)' : 'var(--text-muted)',
                    borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                    borderBottomWidth: '2px', borderBottomStyle: 'solid',
                    borderBottomColor: sidebarTab === tab ? 'var(--green)' : 'transparent',
                    marginBottom: '-1px', background: 'transparent', cursor: 'pointer',
                    fontFamily: 'DM Sans, sans-serif',
                  }}>
                    {tab === 'poster' ? '📣 Poster' : '🌱 Responder'}
                  </button>
                ))}
              </div>

              {sidebarTab === 'poster' && (
                <div style={{ padding: '10px' }}>
                  <div style={{ maxHeight: sidebarExpanded ? 'none' : '380px', overflowY: sidebarExpanded ? 'visible' : 'auto' }}>
                  {myPosts.length === 0 ? (
                    <p style={{ fontSize: '12px', color: 'var(--text-dim)', fontStyle: 'italic', padding: '8px' }}>No posts yet.</p>
                  ) : myPosts.map(p => {
                    const closed = p.is_open === false || (!!p.closes_at && new Date(p.closes_at).getTime() < Date.now());
                    return (
                    <Link key={p.id} href={`/post/${p.id}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', borderRadius: '8px', textDecoration: 'none', transition: 'background 0.15s' }}
                      onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--surface-raised)'}
                      onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}
                    >
                      <div style={{ width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0, background: clarityMap[p.id]?.color ?? 'var(--text-dim)' }} />
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0, width: '16px', textAlign: 'center' }}>{TYPE_SYMBOLS[p.template_type]}</div>
                      <div style={{ flex: 1, fontSize: '12px', color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.prompt}</div>
                      {closed && (
                        <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--danger)', background: 'rgba(196,122,122,0.12)', border: '1px solid rgba(196,122,122,0.3)', borderRadius: '8px', padding: '1px 6px', flexShrink: 0 }}>
                          Closed
                        </span>
                      )}
                      <div style={{ fontSize: '11px', color: 'var(--text-dim)', fontStyle: 'italic', flexShrink: 0 }}>{p.response_count}</div>
                    </Link>
                    );
                  })}
                  </div>
                  {myPosts.length > 5 && (
                    <button
                      onClick={() => setSidebarExpanded(e => !e)}
                      style={{
                        width: '100%', textAlign: 'center', fontSize: '11px', color: 'var(--indigo)',
                        background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0 2px',
                        fontFamily: 'DM Sans, sans-serif',
                      }}
                    >
                      {sidebarExpanded ? '↑ Show less' : `Show all ${myPosts.length} →`}
                    </button>
                  )}
                  <div style={{ padding: '4px 8px 10px', fontSize: '11px', lineHeight: 1.6, display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    <span style={{ color: 'var(--green)' }}>✦ clear</span>
                    <span style={{ color: 'var(--text-dim)', fontStyle: 'italic' }}>·</span>
                    <span style={{ color: 'var(--amber)' }}>~ mixed</span>
                    <span style={{ color: 'var(--text-dim)', fontStyle: 'italic' }}>·</span>
                    <span style={{ color: 'var(--danger)' }}>≠ contested</span>
                    <span style={{ color: 'var(--text-dim)', fontStyle: 'italic' }}>·</span>
                    <span style={{ color: 'var(--text-dim)', fontStyle: 'italic' }}>○ pending</span>
                  </div>
                </div>
              )}

              {sidebarTab === 'responder' && (
                <div style={{ padding: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6px' }}>
                    <AnsweredToggle active={showAnswered} onClick={() => setShowAnswered(v => !v)} />
                  </div>
                  <div style={{ maxHeight: sidebarExpanded ? 'none' : '380px', overflowY: sidebarExpanded ? 'visible' : 'auto' }}>
                  {(() => {
                    const source = showAnswered ? answeredPosts : respondQueue;
                    const filtered = source.filter(p => !activeTag || (p.tags && p.tags.length > 0 && matchesBroadTag(p.tags, activeTag)));
                    if (filtered.length === 0) {
                      return (
                        <p style={{ fontSize: '12px', color: 'var(--text-dim)', fontStyle: 'italic', padding: '8px' }}>
                          {showAnswered
                            ? (activeTag ? `No answered posts tagged "${activeTag}" yet.` : "You haven't answered anything yet.")
                            : (activeTag ? `No posts tagged "${activeTag}" to respond to.` : "You've responded to everything. Nice work.")}
                        </p>
                      );
                    }
                    return (sidebarExpanded ? filtered : filtered.slice(0, 20)).map(p => (
                        <Link key={p.id} href={`/post/${p.id}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', borderRadius: '8px', textDecoration: 'none', transition: 'background 0.15s' }}
                          onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--surface-raised)'}
                          onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}
                        >
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0, width: '16px', textAlign: 'center' }}>{TYPE_SYMBOLS[p.template_type]}</div>
                          <div style={{ flex: 1, fontSize: '12px', color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.prompt}</div>
                          {p.tags && p.tags.length > 0 && (
                            <div style={{ fontSize: '11px', color: 'var(--text-dim)', fontStyle: 'italic', flexShrink: 0 }}>{p.tags[0]}</div>
                          )}
                        </Link>
                      ));
                  })()}
                  </div>
                  {(() => {
                    const source = showAnswered ? answeredPosts : respondQueue;
                    const count = source.filter(p => !activeTag || (p.tags && p.tags.length > 0 && matchesBroadTag(p.tags, activeTag))).length;
                    if (count <= 20) return null;
                    return (
                      <button
                        onClick={() => setSidebarExpanded(e => !e)}
                        style={{
                          width: '100%', textAlign: 'center', fontSize: '11px', color: 'var(--indigo)',
                          background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0 2px',
                          fontFamily: 'DM Sans, sans-serif',
                        }}
                      >
                        {sidebarExpanded ? '↑ Show less' : `Show all ${count} →`}
                      </button>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>

        </div>

        {dashboardOpen && (
          <div
            onClick={() => setDashboardOpen(false)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '32px 20px', zIndex: 60,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius)', width: '100%', maxWidth: '820px',
                maxHeight: '85vh', display: 'flex', flexDirection: 'column',
                boxShadow: '0 24px 60px rgba(0,0,0,0.35)', overflow: 'hidden',
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0, gap: '12px' }}>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {(['poster', 'responder'] as const).map(tab => (
                    <button key={tab} onClick={() => setSidebarTab(tab)} style={{
                      padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                      color: sidebarTab === tab ? 'var(--green)' : 'var(--text-muted)',
                      background: sidebarTab === tab ? 'var(--green-glow)' : 'transparent',
                      border: 'none', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                    }}>
                      {tab === 'poster' ? '📣 Poster' : '🌱 Responder'}
                    </button>
                  ))}
                  {sidebarTab === 'responder' && (
                    <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '4px' }}>
                      <AnsweredToggle active={showAnswered} onClick={() => setShowAnswered(v => !v)} />
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setDashboardOpen(false)}
                  aria-label="Close"
                  style={{
                    background: 'var(--surface-raised)', border: '1px solid var(--border)',
                    borderRadius: '50%', width: '30px', height: '30px', flexShrink: 0, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text)',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M5 5l14 14M19 5L5 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              {/* Body — the full, untruncated list, same data as the sidebar */}
              <div style={{ overflowY: 'auto', padding: '16px 20px', flex: 1 }}>
                {sidebarTab === 'poster' ? (
                  myPosts.length === 0 ? (
                    <p style={{ fontSize: '13px', color: 'var(--text-dim)', fontStyle: 'italic', padding: '20px', textAlign: 'center' }}>No posts yet.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {myPosts.map(p => {
                        const closed = p.is_open === false || (!!p.closes_at && new Date(p.closes_at).getTime() < Date.now());
                        return (
                          <Link key={p.id} href={`/post/${p.id}`} style={{
                            display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px',
                            borderRadius: '10px', textDecoration: 'none', border: '1px solid var(--border)',
                            transition: 'background 0.15s',
                          }}
                            onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--surface-raised)'}
                            onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}
                          >
                            <div style={{ width: '9px', height: '9px', borderRadius: '50%', flexShrink: 0, background: clarityMap[p.id]?.color ?? 'var(--text-dim)' }} />
                            <div style={{ fontSize: '13px', color: 'var(--text-muted)', flexShrink: 0, width: '20px', textAlign: 'center' }}>{TYPE_SYMBOLS[p.template_type]}</div>
                            <div style={{ flex: 1, fontSize: '14px', color: 'var(--text)', fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.prompt}</div>
                            {closed && (
                              <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--danger)', background: 'rgba(196,122,122,0.12)', border: '1px solid rgba(196,122,122,0.3)', borderRadius: '8px', padding: '2px 8px', flexShrink: 0 }}>
                                Closed
                              </span>
                            )}
                            <div style={{ fontSize: '12px', color: 'var(--text-dim)', fontStyle: 'italic', flexShrink: 0, minWidth: '70px', textAlign: 'right' }}>{p.response_count} responses</div>
                          </Link>
                        );
                      })}
                    </div>
                  )
                ) : (
                  (() => {
                    const source = showAnswered ? answeredPosts : respondQueue;
                    const filtered = source.filter(p => !activeTag || (p.tags && p.tags.length > 0 && matchesBroadTag(p.tags, activeTag)));
                    if (filtered.length === 0) {
                      return (
                        <p style={{ fontSize: '13px', color: 'var(--text-dim)', fontStyle: 'italic', padding: '20px', textAlign: 'center' }}>
                          {showAnswered
                            ? (activeTag ? `No answered posts tagged "${activeTag}" yet.` : "You haven't answered anything yet.")
                            : (activeTag ? `No posts tagged "${activeTag}" to respond to.` : "You've responded to everything. Nice work.")}
                        </p>
                      );
                    }
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {filtered.map(p => (
                          <Link key={p.id} href={`/post/${p.id}`} style={{
                            display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px',
                            borderRadius: '10px', textDecoration: 'none', border: '1px solid var(--border)',
                            transition: 'background 0.15s',
                          }}
                            onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--surface-raised)'}
                            onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}
                          >
                            <div style={{ fontSize: '13px', color: 'var(--text-muted)', flexShrink: 0, width: '20px', textAlign: 'center' }}>{TYPE_SYMBOLS[p.template_type]}</div>
                            <div style={{ flex: 1, fontSize: '14px', color: 'var(--text)', fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.prompt}</div>
                            {p.tags && p.tags.length > 0 && (
                              <div style={{ fontSize: '11px', color: 'var(--text-dim)', fontStyle: 'italic', flexShrink: 0 }}>{p.tags[0]}</div>
                            )}
                          </Link>
                        ))}
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
}
